import { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../lib/api-client';

type RegistryEntry = { count: number; intervalMs: number };
const registry: Map<string, RegistryEntry> = new Map();
let timer: ReturnType<typeof setInterval> | null = null;
let activeUserId: string | null = null;
let immediateSendQueued = false;

function computeUserActivityKey(user: { id?: string; email?: string } | null | undefined): string | null {
  const id = String(user?.id || '').trim();
  const email = String(user?.email || '').trim();
  // Activity tracking only needs a stable per-user key. In some auth modes `user.id` may be missing,
  // but email is still present; fallback to email so cache refresh doesn't silently break.
  if (id) {
    // Use a stable composite key so backend can (optionally) derive email for user-scoped caches.
    // This is safe because the activity tracker treats the userId as an opaque string key.
    if (email) return `${id}:${email.toLowerCase()}`;
    return id;
  }
  if (email) return email.toLowerCase();
  return null;
}

function computeIntervalMs(): number {
  // Use the fastest requested interval, bounded to avoid pathological spam.
  let min = 5000;
  for (const v of registry.values()) {
    min = Math.min(min, v.intervalMs);
  }
  return Math.max(2000, min);
}

async function sendBatchedHeartbeat(): Promise<void> {
  if (!activeUserId) return;
  const pages = Array.from(registry.entries())
    .filter(([, v]) => v.count > 0)
    .map(([k]) => k);
  if (pages.length === 0) return;

  try {
    await apiClient.post('/api/activity/heartbeat', {
      pages,
      userId: activeUserId,
    });
  } catch (error) {
    // Silently fail - heartbeat is not critical
    console.debug('Failed to send activity heartbeat:', error);
  }
}

function queueImmediateHeartbeat(): void {
  if (immediateSendQueued) return;
  immediateSendQueued = true;
  queueMicrotask(() => {
    immediateSendQueued = false;
    void sendBatchedHeartbeat();
  });
}

function ensureTimerRunning(): void {
  if (timer) return;
  const intervalMs = computeIntervalMs();
  timer = setInterval(() => void sendBatchedHeartbeat(), intervalMs);
}

function stopTimerIfIdle(): void {
  const any = Array.from(registry.values()).some(v => v.count > 0);
  if (any) return;
  if (timer) {
    clearInterval(timer);
    timer = null;
  }
}

/**
 * Custom hook to send activity heartbeats to the backend
 * This allows the backend to track active users per page and implement adaptive cache TTL
 * 
 * @param pageName - The name of the page (e.g., 'tenants', 'dashboard')
 * @param enabled - Whether to enable the heartbeat (default: true)
 * @param interval - Heartbeat interval in milliseconds (default: 5000 = 5 seconds)
 */
export function useActivityHeartbeat(
  pageName: string,
  enabled: boolean = true,
  interval: number = 5000
) {
  const { user } = useAuth();
  const mountedRef = useRef(false);

  useEffect(() => {
    // Register/unregister this page key; batching ensures only one request goes out.
    if (!enabled || !user || !pageName) return;

    mountedRef.current = true;
    activeUserId = computeUserActivityKey(user);

    const existing = registry.get(pageName);
    if (existing) {
      existing.count += 1;
      existing.intervalMs = Math.min(existing.intervalMs, interval);
    } else {
      registry.set(pageName, { count: 1, intervalMs: interval });
    }

    // Send immediately once per (re)register so backend reacts quickly.
    queueImmediateHeartbeat();

    // Ensure timer is running
    ensureTimerRunning();

    return () => {
      if (!mountedRef.current) return;
      const cur = registry.get(pageName);
      if (cur) {
        cur.count = Math.max(0, cur.count - 1);
        if (cur.count === 0) registry.delete(pageName);
      }
      stopTimerIfIdle();
    };
  }, [pageName, enabled, user?.id, user?.email, interval]);
}

/**
 * Hook that automatically detects the page name from the current route
 * Maps routes to page names used by the cache system
 */
export function useAutoActivityHeartbeat(enabled: boolean = true) {
  const location = useLocation();
  
  // Map routes to page names
  const pageName = getPageNameFromRoute(location.pathname);
  
  useActivityHeartbeat(pageName, enabled);
}

/**
 * Map route paths to cache page names
 */
function getPageNameFromRoute(pathname: string): string {
  // Remove leading slash and split
  const parts = pathname.split('/').filter(Boolean);
  
  if (parts.length === 0 || parts[0] === '') {
    return 'dashboard';
  }

  const firstPart = parts[0];

  // Map routes to page names
  const routeMap: Record<string, string> = {
    'tenants': 'tenants',
    'dashboard': 'dashboard',
    'cloud': 'instances',
    'instances': 'instances',
    'clusters': 'clusters',
    'mas': 'mas_instances',
    'dev': 'dev_instances',
    'dev-containers': 'dev_instances',
    'dev-images': 'dev_images',
    'costs': 'costs',
    'monitoring': 'monitoring',
    'workflows': 'workflows',
    'admin': getAdminPageName(parts),
  };

  return routeMap[firstPart] || 'dashboard';
}

/**
 * Get specific page name for admin routes
 */
function getAdminPageName(parts: string[]): string {
  if (parts.length < 2) {
    return 'admin';
  }

  const adminPage = parts[1];
  
  const adminMap: Record<string, string> = {
    'users': 'users_groups',
    'groups': 'users_groups',
    'cache': 'cache_management',
    'permission-schema': 'permission_schema',
  };

  return adminMap[adminPage] || 'admin';
}

